import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare, Send, CheckCircle, AlertCircle, ArrowLeft,
  Loader, Mail, User, Tag, Building, Star, Bug, Lightbulb,
  HeadphonesIcon, HelpCircle, BookOpen, Sparkles
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { publicAPI } from '../services/api';
import toast from 'react-hot-toast';

const SUBJECTS = [
  { value: 'Overall platform feature', label: 'Platform Feature', icon: Sparkles, color: 'indigo' },
  { value: 'School material missing/unavailable', label: 'Missing Material', icon: BookOpen, color: 'amber' },
  { value: 'School-related issue', label: 'School Issue', icon: Building, color: 'blue' },
  { value: 'Bug report', label: 'Bug Report', icon: Bug, color: 'red' },
  { value: 'Feature request', label: 'Feature Request', icon: Lightbulb, color: 'yellow' },
  { value: 'Technical support', label: 'Tech Support', icon: HeadphonesIcon, color: 'green' },
  { value: 'Other', label: 'Other', icon: HelpCircle, color: 'slate' },
];

const colorMap = {
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-700', icon: 'text-indigo-500', selBg: 'bg-indigo-600', selText: 'text-white', selBorder: 'border-indigo-600' },
  amber:  { bg: 'bg-amber-50',  border: 'border-amber-300',  text: 'text-amber-700',  icon: 'text-amber-500',  selBg: 'bg-amber-500',  selText: 'text-white', selBorder: 'border-amber-500'  },
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-300',   text: 'text-blue-700',   icon: 'text-blue-500',   selBg: 'bg-blue-600',   selText: 'text-white', selBorder: 'border-blue-600'   },
  red:    { bg: 'bg-red-50',    border: 'border-red-300',    text: 'text-red-700',    icon: 'text-red-500',    selBg: 'bg-red-600',    selText: 'text-white', selBorder: 'border-red-600'    },
  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-700', icon: 'text-yellow-500', selBg: 'bg-yellow-500', selText: 'text-white', selBorder: 'border-yellow-500' },
  green:  { bg: 'bg-green-50',  border: 'border-green-300',  text: 'text-green-700',  icon: 'text-green-500',  selBg: 'bg-green-600',  selText: 'text-white', selBorder: 'border-green-600'  },
  slate:  { bg: 'bg-slate-50',  border: 'border-slate-300',  text: 'text-slate-700',  icon: 'text-slate-500',  selBg: 'bg-slate-600',  selText: 'text-white', selBorder: 'border-slate-600'  },
};

const InputField = ({ label, icon: Icon, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" /> {label}
    </label>
    {children}
  </div>
);

const FeedbackPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const ArrayUser = JSON.parse(localStorage.getItem('user') || 'null');
  const [schools, setSchools] = useState([]);

  const [formData, setFormData] = useState({
    fullName: ArrayUser?.fullName || '',
    email: ArrayUser?.email || '',
    subject: '',
    schoolId: '',
    message: '',
    userId: ArrayUser?._id || null,
  });

  useEffect(() => {
    publicAPI.getSchools()
      .then(res => setSchools(res.data?.schools || res.data || []))
      .catch(() => {});
  }, []);

  const isSchoolRelated =
    formData.subject === 'School material missing/unavailable' ||
    formData.subject === 'School-related issue';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (isSchoolRelated && !formData.schoolId) {
      toast.error('Please select the relevant school');
      return;
    }
    try {
      setLoading(true);
      await publicAPI.submitFeedback({ ...formData });
      setSuccess(true);
      toast.success('Thank you! Your feedback has been sent.');
    } catch (err) {
      toast.error(err.message || 'Failed to send feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none bg-white transition-all text-slate-800 placeholder-slate-400 text-sm";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <Header />

      <div className="container mx-auto px-4 py-10 max-w-5xl">
        <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-8 font-semibold text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="grid md:grid-cols-5 gap-0 bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100">

          {/* Left panel */}
          <div className="md:col-span-2 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-8 md:p-10 text-white flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 bg-white/15 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6 border border-white/20">
                <MessageSquare className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-2xl font-black mb-3 leading-tight">We Value<br/>Your Feedback</h1>
              <p className="text-blue-100 text-sm leading-relaxed mb-8">
                Your voice helps us build a better platform for all DDU students and staff. Every message is read by our team.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Lightbulb, color: 'text-yellow-300', text: 'Feature suggestions & ideas' },
                  { icon: Bug, color: 'text-red-300', text: 'Bug reports & technical issues' },
                  { icon: BookOpen, color: 'text-green-300', text: 'Missing or incorrect materials' },
                  { icon: Mail, color: 'text-blue-300', text: 'General inquiries & outreach' },
                ].map(({ icon: Icon, color, text }) => (
                  <div key={text} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                      <Icon className={`w-4 h-4 ${color}`} />
                    </div>
                    <span className="text-sm text-blue-100">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/20">
              <p className="text-[10px] font-black uppercase tracking-widest text-blue-300 mb-3">Response Time</p>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-sm text-white font-medium">Usually within 1–2 business days</span>
              </div>
            </div>
          </div>

          {/* Right panel — form */}
          <div className="md:col-span-3 p-8 md:p-10">
            <AnimatePresence mode="wait">
              {success ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-full flex flex-col items-center justify-center text-center py-10"
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-2">Message Sent!</h2>
                  <p className="text-slate-500 text-sm mb-8 max-w-sm">
                    Thank you for reaching out. Our administrators will review your feedback and respond if needed.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => { setSuccess(false); }}
                      className="px-6 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50 transition-all"
                    >
                      Send Another
                    </button>
                    <button
                      onClick={() => navigate('/')}
                      className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20"
                    >
                      Back to Home
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h2 className="text-xl font-black text-slate-900 mb-1">Send Us a Message</h2>
                  <p className="text-slate-400 text-sm mb-7">All fields marked with * are required.</p>

                  <form onSubmit={handleSubmit} className="space-y-5">

                    {/* Name + Email row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <InputField label="Full Name *" icon={User}>
                        <input
                          type="text" name="fullName"
                          value={formData.fullName} onChange={handleChange}
                          placeholder="Your full name"
                          className={inputClass} required
                          readOnly={!!ArrayUser?.fullName}
                        />
                      </InputField>
                      <InputField label="Email Address *" icon={Mail}>
                        <input
                          type="email" name="email"
                          value={formData.email} onChange={handleChange}
                          placeholder="you@example.com"
                          className={inputClass} required
                          readOnly={!!ArrayUser?.email}
                        />
                      </InputField>
                    </div>

                    {/* Subject chips */}
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                        <Tag className="w-3.5 h-3.5" /> Category *
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {SUBJECTS.map(({ value, label, icon: Icon, color }) => {
                          const c = colorMap[color];
                          const selected = formData.subject === value;
                          return (
                            <button
                              type="button" key={value}
                              onClick={() => setFormData(prev => ({ ...prev, subject: value, schoolId: '' }))}
                              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                                selected
                                  ? `${c.selBg} ${c.selText} ${c.selBorder} shadow-sm`
                                  : `bg-white ${c.border} ${c.text} hover:${c.bg}`
                              }`}
                            >
                              <Icon className="w-3.5 h-3.5" />
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* School selector — animated */}
                    <AnimatePresence>
                      {isSchoolRelated && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <InputField label="Target School *" icon={Building}>
                            <select
                              name="schoolId" value={formData.schoolId}
                              onChange={handleChange} required
                              className={inputClass}
                            >
                              <option value="">Select the school involved...</option>
                              {schools.map(s => (
                                <option key={s._id} value={s._id}>{s.name}</option>
                              ))}
                            </select>
                            <p className="text-xs text-blue-500 mt-1 ml-1">
                              This feedback will be routed to that school's administration.
                            </p>
                          </InputField>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Message */}
                    <InputField label="Message *" icon={MessageSquare}>
                      <textarea
                        name="message" value={formData.message}
                        onChange={handleChange}
                        placeholder="Describe your feedback, issue, or suggestion in detail..."
                        rows={4}
                        className={`${inputClass} resize-none`}
                        required
                      />
                    </InputField>



                    {/* Submit */}
                    <button
                      type="submit" disabled={loading}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black py-3.5 rounded-xl shadow-lg shadow-blue-600/25 hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center justify-center gap-2.5 disabled:opacity-60 disabled:cursor-not-allowed text-sm uppercase tracking-widest"
                    >
                      {loading ? (
                        <><Loader className="w-4 h-4 animate-spin" /> Sending...</>
                      ) : (
                        <><Send className="w-4 h-4" /> Send Feedback</>
                      )}
                    </button>

                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FeedbackPage;
